import { clientMoveIn } from "./clientMoveIn";
// Ch78 · THE MOVE-IN BOARD — pure model, no React.
//
// Grant (2026-09-08): "a widget, interactive, grouped & zoomable … kinda like
// a calendar of move-in dates, maybe paginated by month … I need a BETTER WAY
// to VISUALIZE ALL LEADS/CLIENTS."
//
// The one date the business runs on is move-in: a person's value expires at
// it. So the axis is TIME, paged by month and zoomable (month → days,
// quarter → weeks, year → months); the rows are SWIMLANES (by stage, by
// locator, or by neighborhood); each person is a PILL on their move-in day,
// coloured by confidence, sized by budget. Zoomed out, pills merge into
// bubbles ("12 · $38k"). Two honest gutters: PAST DUE (the date passed and
// nobody was placed) and NO DATE (they never gave one). Nothing is guessed
// onto the axis — free text like "August" is a NO DATE row with its words.
//
// Leads AND clients share the board. A converted lead and its client are the
// same person: the client row wins (it carries the stage and the locator),
// and the lead's typed date fills in when the client's move-in is free text.

import type { Agent, Client, Lead } from "../components/clients/types";
import { statusConfig } from "../components/clients/config";
import { leadIntelTier, type LeadIntelTier } from "../components/clients/leadQuality";
import { normalizeLeadNeighborhoods } from "./leadPreferences";
import { leadBudgetNumber, parseLeadMoveInDate, tierTone, type TimelineTone } from "./leadsTimeline";

export type BoardZoom = "month" | "quarter" | "year";
export type BoardGroupBy = "stage" | "agent" | "neighborhood";

/**
 * The identity two rows must share to be ONE person: normalized email.
 *
 * Not invented here — it is the rule `lead_flow_truth.returning_renters`
 * already uses to say a lead IS an existing client, so the board and the
 * backend agree about who is who. Phone is deliberately NOT part of it: a
 * shared household number would merge two roommates into one pill, and a
 * false merge silently deletes a person from the page, which is worse than
 * the duplicate it would fix.
 */
export function identityKey(email: string | null | undefined): string | null {
  const key = (email || "").trim().toLowerCase();
  return key || null;
}

export interface BoardPerson {
  /** Stable key: `lead:<id>` or `client:<id>`. */
  key: string;
  kind: "lead" | "client";
  name: string;
  /** Local-midnight ms of the move-in day, or null (NO DATE). */
  dateMs: number | null;
  /** What they actually wrote, for the NO DATE gutter and tooltips. */
  moveInText: string | null;
  budget: number | null;
  tier: LeadIntelTier | null;
  tone: TimelineTone;
  /** Client status, or "lead" for an unconverted lead. */
  stage: string;
  agentId: string | null;
  agentName: string | null;
  neighborhoods: string[];
  textsOk: boolean;
  /** How many source rows collapsed into this person. 1 unless they are a dupe. */
  rowCount: number;
  /**
   * They came back: a lead arrived AFTER this client row existed. A returning
   * renter is one of the most valuable rows on the page — somebody who already
   * knows us came back — so they are LABELED, never hidden (the rule
   * lead_flow_truth.returning_renters exists to enforce).
   */
  returnedAt: string | null;
  lead?: Lead;
  client?: Client;
}

const DAY_MS = 86_400_000;
const PLACED_STAGES = new Set(["payment_received", "closed"]);

function midnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

type ClientWithLineage = Client & {
  origin_lead_id?: string | null;
  move_in_date?: string | null;
  preferred_areas?: string[];
  sms_opted_in?: boolean | null;
};

/** Every lead and client as one list of people, deduped through the lead link. */
export function buildBoardPeople(leads: Lead[], clients: Client[], agents: Agent[]): BoardPerson[] {
  const agentName = new Map(agents.map((a) => [a.id, a.full_name || a.email]));
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const claimedLeadIds = new Set<string>();
  const out: BoardPerson[] = [];

  // Every lead that shares an email with something else, newest first, so a
  // client claims the most RECENT inquiry as its origin and the older ones
  // collapse into it rather than standing as separate people on the calendar.
  const leadsByIdentity = new Map<string, Lead[]>();
  for (const lead of leads) {
    const key = identityKey(lead.email);
    if (!key) continue;
    const bucket = leadsByIdentity.get(key);
    if (bucket) bucket.push(lead);
    else leadsByIdentity.set(key, [lead]);
  }
  for (const bucket of leadsByIdentity.values()) {
    bucket.sort((a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || ""));
  }

  for (const raw of clients as ClientWithLineage[]) {
    // The lineage column first, then the estate's identity rule. A client
    // converted without `origin_lead_id` used to stand beside its own lead as
    // two people on one day — visible in production the hour this page shipped.
    const sameEmail = leadsByIdentity.get(identityKey(raw.email) ?? "") ?? [];
    const origin = (raw.origin_lead_id ? leadById.get(raw.origin_lead_id) : undefined) ?? sameEmail[0];
    // EVERY lead sharing this identity is this person, not just the origin.
    const merged = new Set<Lead>(sameEmail);
    if (origin) merged.add(origin);
    for (const l of merged) claimedLeadIds.add(l.id);
    // A client's move-in is free text ("Oct 1", "0-30 days"); try it as a
    // date, then fall back to the lead form's typed date, else NO DATE.
    const ownDate = parseLeadMoveInDate(raw.move_in_date ?? raw.move_in_timeline ?? null);
    const leadDate = origin ? parseLeadMoveInDate(origin.move_in_date) : null;
    const date = ownDate ?? leadDate;
    const tier = origin ? leadIntelTier(origin) : null;
    const budget =
      typeof raw.max_budget === "number" && raw.max_budget > 0
        ? raw.max_budget
        : origin
          ? leadBudgetNumber(origin)
          : null;
    const areas = (raw.preferred_areas && raw.preferred_areas.length ? raw.preferred_areas : null) ??
      (origin ? normalizeLeadNeighborhoods(origin.neighborhoods) : []);
    // Newest inquiry after the client row already existed = they came back.
    const clientCreated = Date.parse(raw.created_at || "");
    const newestLead = origin ? Date.parse(origin.created_at || "") : NaN;
    const returnedAt =
      Number.isFinite(clientCreated) && Number.isFinite(newestLead) && newestLead > clientCreated
        ? (origin as Lead).created_at
        : null;
    out.push({
      key: `client:${raw.id}`,
      kind: "client",
      rowCount: 1 + merged.size,
      returnedAt,
      name: `${raw.first_name || ""} ${raw.last_name || ""}`.trim() || "Unnamed client",
      dateMs: date ? date.getTime() : null,
      moveInText: clientMoveIn(raw) ?? origin?.move_in_date ?? null,
      budget,
      tier,
      tone: tierTone(tier),
      stage: (raw.status || "new_client").toLowerCase(),
      agentId: raw.assigned_agent_id ?? null,
      agentName: raw.assigned_agent_id ? agentName.get(raw.assigned_agent_id) ?? null : null,
      neighborhoods: areas,
      textsOk: raw.sms_opted_in === true || origin?.sms_opted_in === true,
      client: raw,
      lead: origin,
    });
  }

  // Leads with no client. Two form submissions from one email are ONE person:
  // the newest carries the story, the older ones are counted, not drawn twice.
  const emittedIdentities = new Set<string>();
  for (const lead of leads) {
    if (claimedLeadIds.has(lead.id)) continue; // the client row IS this person
    const key = identityKey(lead.email);
    if (key) {
      if (emittedIdentities.has(key)) continue;
      emittedIdentities.add(key);
    }
    const siblings = key
      ? (leadsByIdentity.get(key) ?? []).filter((l) => !claimedLeadIds.has(l.id))
      : [lead];
    // The NEWEST submission describes them today; the older ones only
    // contribute facts the newest left blank. A renter who re-submitted with a
    // date must not be drawn on the day they gave three months ago.
    const primary = siblings[0] ?? lead;
    const firstStated = <T,>(pick: (l: Lead) => T | null | undefined): T | null => {
      for (const l of siblings.length ? siblings : [lead]) {
        const value = pick(l);
        if (value !== null && value !== undefined) return value;
      }
      return null;
    };
    const date = parseLeadMoveInDate(primary.move_in_date) ?? firstStated((l) => parseLeadMoveInDate(l.move_in_date));
    const tier = leadIntelTier(primary) ?? firstStated((l) => leadIntelTier(l));
    out.push({
      key: `lead:${primary.id}`,
      kind: "lead",
      name: (primary.name || "").trim() || "Unknown",
      dateMs: date ? date.getTime() : null,
      moveInText: primary.move_in_date ?? firstStated((l) => l.move_in_date) ?? null,
      budget: leadBudgetNumber(primary) ?? firstStated((l) => leadBudgetNumber(l)),
      tier,
      tone: tierTone(tier),
      stage: primary.status === "converted" ? "new_client" : "lead",
      agentId: null,
      agentName: null,
      neighborhoods: normalizeLeadNeighborhoods(primary.neighborhoods),
      // Consent is a yes they gave at least ONCE, on any submission.
      textsOk: (siblings.length ? siblings : [lead]).some((l) => l.sms_opted_in === true),
      rowCount: Math.max(1, siblings.length),
      returnedAt: null,
      lead: primary,
    });
  }
  return out;
}

// ---------------------------------------------------------------- windows

/** Snap any date to the start of its month / quarter / year. */
export function snapAnchor(date: Date, zoom: BoardZoom): Date {
  const y = date.getFullYear();
  const m = date.getMonth();
  if (zoom === "month") return new Date(y, m, 1);
  if (zoom === "quarter") return new Date(y, m - (m % 3), 1);
  return new Date(y, 0, 1);
}

export function shiftAnchor(anchor: Date, zoom: BoardZoom, delta: number): Date {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (zoom === "month") return new Date(y, m + delta, 1);
  if (zoom === "quarter") return new Date(y, m + 3 * delta, 1);
  return new Date(y + delta, 0, 1);
}

export function windowEnd(anchor: Date, zoom: BoardZoom): Date {
  return shiftAnchor(anchor, zoom, 1);
}

const MONTH_LONG = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const MONTH_SHORT = new Intl.DateTimeFormat("en-US", { month: "short" });
const DAY_SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

export function windowLabel(anchor: Date, zoom: BoardZoom): string {
  if (zoom === "month") return MONTH_LONG.format(anchor);
  if (zoom === "quarter") return `Q${Math.floor(anchor.getMonth() / 3) + 1} ${anchor.getFullYear()}`;
  return String(anchor.getFullYear());
}

export interface BoardColumn {
  startMs: number;
  endMs: number; // exclusive
  label: string;
  /** Month zoom: the weekday initial; used to shade weekends. */
  weekend?: boolean;
  isToday?: boolean;
}

export function buildColumns(anchor: Date, zoom: BoardZoom, now: Date): BoardColumn[] {
  const todayMs = midnight(now);
  const end = windowEnd(anchor, zoom).getTime();
  const cols: BoardColumn[] = [];
  if (zoom === "month") {
    for (let t = anchor.getTime(); t < end; ) {
      const d = new Date(t);
      const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
      cols.push({
        startMs: t,
        endMs: next,
        label: String(d.getDate()),
        weekend: d.getDay() === 0 || d.getDay() === 6,
        isToday: t === todayMs,
      });
      t = next;
    }
  } else if (zoom === "quarter") {
    for (let t = anchor.getTime(); t < end; ) {
      const next = Math.min(t + 7 * DAY_MS, end);
      cols.push({ startMs: t, endMs: next, label: DAY_SHORT.format(new Date(t)), isToday: todayMs >= t && todayMs < next });
      t = next;
    }
  } else {
    for (let m = 0; m < 12; m++) {
      const s = new Date(anchor.getFullYear(), m, 1);
      const e = new Date(anchor.getFullYear(), m + 1, 1);
      cols.push({ startMs: s.getTime(), endMs: e.getTime(), label: MONTH_SHORT.format(s), isToday: todayMs >= s.getTime() && todayMs < e.getTime() });
    }
  }
  return cols;
}

// ------------------------------------------------------------------ lanes

const STAGE_ORDER = ["lead", "new_client", "touring", "applied", "payment_received", "closed", "out_of_contact"];

export function stageLabel(stage: string): string {
  if (stage === "lead") return "Lead";
  return statusConfig[stage as keyof typeof statusConfig]?.label ?? stage.replace(/_/g, " ");
}

function laneKeyFor(p: BoardPerson, groupBy: BoardGroupBy): { key: string; label: string } {
  if (groupBy === "stage") return { key: p.stage, label: stageLabel(p.stage) };
  if (groupBy === "agent") {
    return p.agentId ? { key: p.agentId, label: p.agentName || "Agent" } : { key: "__unassigned", label: "Unassigned" };
  }
  const area = p.neighborhoods[0];
  return area ? { key: area.toLowerCase(), label: area } : { key: "__noarea", label: "No area" };
}

export interface BoardCell {
  col: number;
  people: BoardPerson[];
  budgetTotal: number;
}

export interface BoardLane {
  key: string;
  label: string;
  count: number;
  cells: BoardCell[]; // only non-empty cells
}

export interface BoardSummary {
  count: number;
  budgetTotal: number;
  byTone: Record<TimelineTone, number>;
}

export interface MoveInBoardModel {
  columns: BoardColumn[];
  lanes: BoardLane[];
  /** Move-in day passed, not placed. Newest-passed first. */
  pastDue: BoardPerson[];
  noDate: BoardPerson[];
  summary: BoardSummary;
  windowLabel: string;
  todayMs: number;
}

function summarize(people: BoardPerson[]): BoardSummary {
  const byTone: Record<TimelineTone, number> = { real: 0, uncertain: 0, fake: 0, unscored: 0 };
  let budgetTotal = 0;
  for (const p of people) {
    byTone[p.tone] += 1;
    budgetTotal += p.budget ?? 0;
  }
  return { count: people.length, budgetTotal, byTone };
}

export function buildMoveInBoard(
  people: BoardPerson[],
  opts: { anchor: Date; zoom: BoardZoom; groupBy: BoardGroupBy; now: Date },
): MoveInBoardModel {
  const anchor = snapAnchor(opts.anchor, opts.zoom);
  const columns = buildColumns(anchor, opts.zoom, opts.now);
  const startMs = columns[0].startMs;
  const endMs = columns[columns.length - 1].endMs;
  const todayMs = midnight(opts.now);

  const inWindow: BoardPerson[] = [];
  const pastDue: BoardPerson[] = [];
  const noDate: BoardPerson[] = [];
  for (const p of people) {
    if (p.dateMs === null) noDate.push(p);
    else if (p.dateMs < todayMs && !PLACED_STAGES.has(p.stage)) pastDue.push(p);
    else if (p.dateMs >= startMs && p.dateMs < endMs) inWindow.push(p);
  }
  pastDue.sort((a, b) => (b.dateMs ?? 0) - (a.dateMs ?? 0));

  const laneMap = new Map<string, BoardLane>();
  for (const p of inWindow) {
    const { key, label } = laneKeyFor(p, opts.groupBy);
    let lane = laneMap.get(key);
    if (!lane) {
      lane = { key, label, count: 0, cells: [] };
      laneMap.set(key, lane);
    }
    lane.count += 1;
    const col = columns.findIndex((c) => (p.dateMs as number) >= c.startMs && (p.dateMs as number) < c.endMs);
    let cell = lane.cells.find((c) => c.col === col);
    if (!cell) {
      cell = { col, people: [], budgetTotal: 0 };
      lane.cells.push(cell);
    }
    cell.people.push(p);
    cell.budgetTotal += p.budget ?? 0;
  }
  const lanes = [...laneMap.values()];
  if (opts.groupBy === "stage") {
    lanes.sort((a, b) => STAGE_ORDER.indexOf(a.key) - STAGE_ORDER.indexOf(b.key));
  } else {
    lanes.sort((a, b) => {
      const ua = a.key.startsWith("__") ? 1 : 0;
      const ub = b.key.startsWith("__") ? 1 : 0;
      return ua - ub || a.label.localeCompare(b.label);
    });
  }
  for (const lane of lanes) {
    lane.cells.sort((a, b) => a.col - b.col);
    for (const cell of lane.cells) cell.people.sort((a, b) => (b.budget ?? 0) - (a.budget ?? 0));
  }

  return {
    columns,
    lanes,
    pastDue,
    noDate,
    summary: summarize(inWindow),
    windowLabel: windowLabel(anchor, opts.zoom),
    todayMs,
  };
}

/** "$38k" / "$950" — the bubble's second number. */
export function compactMoney(n: number): string {
  if (n >= 1000) return `$${Math.round(n / 100) / 10}k`;
  return `$${Math.round(n)}`;
}
